import type { LatLng } from '@cobliteam/api-lib';
import type { Coordinate } from './geoJSON';

const EARTH_RADIUS_IN_KM = 6378.14;
const EARTH_RADIUS_IN_METERS = EARTH_RADIUS_IN_KM * 1000;

const NUMBER_OF_POINTS = 36;
const RADIAN_FACTOR = 360 / NUMBER_OF_POINTS;

const PRECISION = 10000000;
const PI_RADIAN_FACTOR = (180 / Math.PI) * PRECISION;

const convertToRadians = (value: number) => value * (Math.PI / 180);
const piRadian = (value: number) => Math.floor(value * PI_RADIAN_FACTOR) / PRECISION;

export class Haversine {
  public static pointsOnCircle(radius: number, coordinate: Coordinate) {
    const { latitude, longitude } = coordinate;

    const latitudeInRad = convertToRadians(latitude);
    const longitudeInRad = convertToRadians(longitude);

    const dist = radius / EARTH_RADIUS_IN_METERS;
    const coordinates = [];

    for (let i = 1; i <= NUMBER_OF_POINTS; i++) {
      const angle = convertToRadians(i * RADIAN_FACTOR);

      const newLat = Math.asin(Math.sin(latitudeInRad) * Math.cos(dist) + Math.cos(latitudeInRad) * Math.sin(dist) * Math.cos(angle));

      const newLng = longitudeInRad + Math.atan2(Math.sin(angle) * Math.sin(dist) * Math.cos(newLat), Math.cos(dist) - Math.sin(newLat) * Math.sin(newLat));

      const newLatInRad = piRadian(newLat);
      const newLngInRad = piRadian(newLng);

      coordinates.push([newLngInRad, newLatInRad]);
    }

    const first = coordinates[0];
    const closedPolygon = [...coordinates, first];
    return closedPolygon;
  }

  /*
   * a = sin²(Δφ/2) + cos(φ₁) * cos(φ₂) * sin²(Δλ/2)
   * c = 2 * atan²(√a, √(1 - a))
   * d = R * c
   *
   * Where:
   * φ = latitude
   * λ = longitude
   * R = Earth's radius (R = 6371km)
   */
  public static kmDistanceBetweenPoints = (point1: LatLng, point2: LatLng) => {
    const lat = point2.lat - point1.lat;
    const lng = point2.lng - point1.lng;
    const latInRad = convertToRadians(lat);
    const lngInRad = convertToRadians(lng);
    const a = Math.sin(latInRad / 2) ** 2 + Math.cos(convertToRadians(point1.lat)) * Math.cos(convertToRadians(point2.lat)) * Math.sin(lngInRad / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_IN_KM * c;
  };

  /**
   * Finds the middle point of a line based on distance traveled.
   * This is different from the geometric center (centroid) as it accounts for the actual
   * distance traveled along the line segments.
   *
   * @param points Array of points forming the line, each point having lat and lng properties
   * @returns The middle point of the line with lat and lng properties
   */
  public static findMiddlePoint = (points: LatLng[]): LatLng => {
    if (points.length < 2) {
      return points[0] || { lat: 0, lng: 0 };
    }

    // Calculate total distance and distances to each point
    let totalDistance = 0;
    const distances: number[] = [0];

    for (let i = 1; i < points.length; i++) {
      const segmentDistance = Haversine.kmDistanceBetweenPoints(points[i - 1], points[i]);
      totalDistance += segmentDistance;
      distances.push(totalDistance);
    }

    // Find the point closest to the middle distance
    const middleDistance = totalDistance / 2;
    let middlePointIndex = 0;

    for (let i = 0; i < distances.length; i++) {
      if (distances[i] <= middleDistance && (i === distances.length - 1 || distances[i + 1] > middleDistance)) {
        middlePointIndex = i;
        break;
      }
    }

    // If we're not exactly at the middle distance, interpolate between points
    if (middlePointIndex < points.length - 1) {
      const currentDist = distances[middlePointIndex];
      const nextDist = distances[middlePointIndex + 1];
      const ratio = (middleDistance - currentDist) / (nextDist - currentDist);

      const p1 = points[middlePointIndex];
      const p2 = points[middlePointIndex + 1];

      return {
        lat: p1.lat + (p2.lat - p1.lat) * ratio,
        lng: p1.lng + (p2.lng - p1.lng) * ratio,
      };
    }

    return points[middlePointIndex];
  };
}
